import {
  Box,
  Grid,
  Hidden,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import {
  OpeningActivity,
  OpeningsService,
  PaginateJsonSchema,
} from 'api-clients/monolith';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';

import GlobalNav from 'components/GlobalNav';

import messages from './messages';
import { useStyles } from './styles';

export const OpeningActivities: React.FC = () => {
  const intl = useIntl();
  const classes = useStyles();
  const history = useHistory();
  const { accountSlug, funnelExternalId } = useParams<{
    accountSlug: string;
    funnelExternalId: string;
  }>();

  const [activities, setActivities] = useState<OpeningActivity[]>([]);
  const [pagination, setPagination] = useState<PaginateJsonSchema>({
    current_page: 1,
    next_page: null,
    total_pages: 1,
    total_count: 0,
    per_page: 25,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchActivities = React.useCallback(
    async (pageNumber: number, perPage: number) => {
      try {
        setLoading(true);
        setError(null);

        const response = await OpeningsService.getInternalApiOpeningsActivities(
          funnelExternalId,
          pageNumber,
          perPage,
        );

        setActivities(response.activities);
        setPagination(response.pagination);
      } catch (err) {
        setError(intl.formatMessage(messages.errorLoadingActivities));
      } finally {
        setLoading(false);
      }
    },
    [funnelExternalId, intl],
  );

  useEffect(() => {
    void fetchActivities(page + 1, rowsPerPage);
  }, [page, rowsPerPage, fetchActivities]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBack = () => {
    history.push(`/${accountSlug}/openings`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Grid container direction="column">
      <Helmet>
        <title>{intl.formatMessage(messages.pageTitle)}</title>
        <meta
          name="description"
          content={intl.formatMessage(messages.pageDescription)}
        />
      </Helmet>

      <Hidden smDown>
        <GlobalNav />
      </Hidden>
      <Grid container className={classes.root}>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Box className={classes.header}>
              <IconButton
                onClick={handleBack}
                className={classes.backButton}
                aria-label="back"
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" component="h1">
                {intl.formatMessage(messages.title)}
              </Typography>
            </Box>

            {error ? (
              <Typography color="error" className={classes.error}>
                {error}
              </Typography>
            ) : (
              <>
                <TableContainer>
                  <Table
                    aria-label={intl.formatMessage(messages.tableAriaLabel)}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          {intl.formatMessage(messages.columnUser)}
                        </TableCell>
                        <TableCell>
                          {intl.formatMessage(messages.columnFieldName)}
                        </TableCell>
                        <TableCell>
                          {intl.formatMessage(messages.columnOldValue)}
                        </TableCell>
                        <TableCell>
                          {intl.formatMessage(messages.columnNewValue)}
                        </TableCell>
                        <TableCell>
                          {intl.formatMessage(messages.columnTimestamp)}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            {intl.formatMessage(messages.loading)}
                          </TableCell>
                        </TableRow>
                      ) : activities.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            {intl.formatMessage(messages.noActivities)}
                          </TableCell>
                        </TableRow>
                      ) : (
                        activities.map(activity => (
                          <TableRow key={activity.id}>
                            <TableCell>{activity.user_name ?? '-'}</TableCell>
                            <TableCell>{activity.field_name ?? '-'}</TableCell>
                            <TableCell>{activity.old_value ?? '-'}</TableCell>
                            <TableCell>{activity.new_value ?? '-'}</TableCell>
                            <TableCell>
                              {formatDate(activity.created_at)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[25, 50, 100]}
                  component="div"
                  count={pagination.total_count}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                />
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default OpeningActivities;
